// =============================================================================
// domain/instrumentFamily.ts — pairing a micro with its full-size sibling.
// -----------------------------------------------------------------------------
// Josh's own request, filed as issue #17:
//
//   "Excursion forces a choice of a single instrument in order to present clean
//    meaningful data. If multiple different instruments were lumped together,
//    stats would be distorted based on wildly varying price scales. I'm proposing
//    that we allow associated Mini and Micro contracts to be displayed together.
//    For example, ES and MES or NQ and MNQ. Since the price scales are identical,
//    performance can be accurately measured in aggregate."
//
// ── 🚨 WHY THIS IS SAFE WHERE "ALL INSTRUMENTS" IS NOT ──
//
// Excursion is measured in POINTS. A 10-point adverse excursion is the same
// market move whether it was taken on ES or MES — only the dollars differ ($500
// against $50). So a median across the pair is a real number, where a median
// across ES and CL is arithmetic on two different scales and means nothing. The
// tab defaults to a single instrument for exactly that reason.
//
// ⚠ AND THE DOLLAR CARDS STAY CORRECT because they report TOTALS, not per-trade
// averages: $500 given back on ES plus $50 on MES really is $550 given back.
// **A per-trade dollar average would NOT survive this grouping** — if one is
// ever added to this tab, it must scope to a single instrument or normalise.
//
// ── 🚨 THE GUARD IS THE TICK SIZE, NOT THE NAME ──
//
// A name rule alone is how a wrong pair gets grouped and quietly distorts the
// very statistic the feature exists to sharpen. Two symbols are only ever
// grouped when the catalogue says their **tick sizes are equal** — which is
// precisely Josh's "the price scales are identical", made checkable.
//
// That guard is what makes the fallback rule below safe to have at all.
// =============================================================================

import type { ContractSpec } from './trade';

/**
 * Micro → its full-size sibling, where the name does not give it away.
 *
 * ⚠ These are the ones an `M` + root rule gets WRONG. `M2K` is the micro Russell
 * against `RTY`; `SIL` is micro silver against `SI`; neither has the shape.
 */
const EXPLICIT_MICRO: Readonly<Record<string, string>> = {
  M2K: 'RTY',
  SIL: 'SI',
  MBT: 'BTC',
  MET: 'ETH',
  MJY: '6J',
  MSF: '6S',
};

/**
 * The full-size sibling of `sym`, or `null` if it is not a micro.
 *
 * ⚠ The `M` + root fallback covers MES→ES, MNQ→NQ, MYM→YM, MGC→GC, MCL→CL,
 * M6E→6E. It is a GUESS, and it is only ever acted on after the tick-size check
 * in `buildInstrumentFamilies` agrees.
 */
export function fullSizeOf(sym: string | null | undefined): string | null {
  const s = String(sym ?? '').trim().toUpperCase();
  if (!s) return null;
  const explicit = EXPLICIT_MICRO[s];
  if (explicit) return explicit;
  // ⚠ At least two characters left, or "M" alone would claim every symbol.
  if (s.length >= 3 && s.startsWith('M')) return s.slice(1);
  return null;
}

export interface InstrumentFamily {
  /** The option's value, e.g. `fam:ES`. Namespaced so it cannot collide. */
  key: string;
  /** What to show, e.g. `ES + MES`. */
  label: string;
  /** Symbols this family scopes to — the full-size one first. */
  members: string[];
  /** Combined trade count, for the selector. */
  count: number;
}

export const FAMILY_PREFIX = 'fam:';

const tickOf = (sym: string, catalogue: readonly ContractSpec[]): number | null => {
  const c = catalogue.find((x) => x.symbol.toUpperCase() === sym.toUpperCase());
  const ts = c ? Number(c.tickSize) : Number.NaN;
  return Number.isFinite(ts) && ts > 0 ? ts : null;
};

/**
 * Families worth offering, given what the user actually traded.
 *
 * ⚠ ONLY WHERE BOTH SIDES ARE PRESENT. Offering "ES + MES" to someone who has
 * never traded ES is a menu entry that changes nothing, and a selector full of
 * those is how a useful option gets lost.
 *
 * ⚠ AND ONLY WHERE THE TICK SIZES MATCH. Either symbol missing from the
 * catalogue means no answer, and no answer means no grouping — silence is the
 * safe direction here, because a wrong pairing distorts the statistic this
 * feature exists to sharpen.
 */
export function buildInstrumentFamilies(
  present: readonly { symbol: string; count: number }[],
  catalogue: readonly ContractSpec[],
): InstrumentFamily[] {
  const counts = new Map<string, number>();
  for (const p of present) counts.set(p.symbol.toUpperCase(), p.count);

  const out: InstrumentFamily[] = [];
  for (const [sym, count] of counts) {
    const full = fullSizeOf(sym);
    if (!full) continue;
    const fullCount = counts.get(full);
    if (fullCount == null) continue;

    const tMicro = tickOf(sym, catalogue);
    const tFull = tickOf(full, catalogue);
    if (tMicro == null || tFull == null || tMicro !== tFull) continue;

    out.push({
      key: `${FAMILY_PREFIX}${full}`,
      label: `${full} + ${sym}`,
      members: [full, sym],
      count: fullCount + count,
    });
  }
  return out.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

/**
 * Does a trade's instrument fall inside the current selection?
 *
 * Handles all three cases the selector can hold: `all`, one symbol, or a family.
 */
export function instrumentMatches(
  contract: string | null | undefined,
  active: string,
  families: readonly InstrumentFamily[],
): boolean {
  if (active === 'all') return true;
  const c = String(contract ?? '').toUpperCase();
  if (!active.startsWith(FAMILY_PREFIX)) return c === active.toUpperCase();
  const fam = families.find((f) => f.key === active);
  // ⚠ An unknown family key matches NOTHING rather than everything. A stale
  // selection — the family disappears when the date range moves off the days
  // that contained one of its members — must empty the cards, not silently
  // widen them to the whole book while still reading "ES + MES".
  if (!fam) return false;
  return fam.members.some((m) => m.toUpperCase() === c);
}
